<script setup lang="ts">
  import { useTemplateRef } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useUser } from '@gat-ship/user'
  import { testIds, type AccessRole } from '@gat-ship/types'
  import {
    Button,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription
  } from '@gat-ship/vue-components'
  import { IconPlus } from '@gat-ship/icons'
  import {
    AccessRolesList,
    AccessRolesModalProvider
  } from '@gat-ship/access-control'

  // The global AdministrationModal opens purely off `?admin=access-roles` and is
  // mounted for every authenticated user, so this panel must gate itself — the
  // router AccessRolesView enforces the same check via PageLayout access-props.
  const user = useUser()

  // Add lives behind AccessRolesModalProvider via the reka collection context,
  // which only descendants may inject — so the panel reaches it through the
  // list's exposed openAdd (the estimated-time-names/ports precedent).
  const list = useTemplateRef<InstanceType<typeof AccessRolesList>>('list')

  function openAdd() {
    list.value?.openAdd()
  }

  const route = useRoute()
  const router = useRouter()

  // From the panel, a row click opens the self-contained access-role detail
  // modal (driven by `?accessRole` via the global AccessRoleDetailRouteProvider),
  // layered over the admin modal — `?admin=access-roles` is preserved.
  function openRole(role: AccessRole) {
    router.push({
      query: {
        ...route.query,
        accessRole: role.id
      }
    })
  }
</script>

<template>
  <div
    v-if="!user.isTenantAdmin.value"
    class="text-muted-foreground p-10 text-center text-sm"
  >
    You don't have permission to manage access roles.
  </div>
  <AccessRolesModalProvider v-else :fetch-on-mount="true">
    <!-- pt-3 offsets the modal's -mt-3 under-fade pull; pb-5 keeps the last
         row off the modal edge (the tenant-users panel precedent). -->
    <div class="flex flex-col gap-6 pb-5 pt-3">
      <ModalSection>
        <ModalSectionHeader>
          <ModalSectionHeaderTitle
            >Access Roles<Button
              class="ml-auto shrink-0"
              :data-testid="testIds.accessRole.actions.add"
              @click="openAdd"
            >
              <IconPlus class="size-4" />
              Add Role
            </Button></ModalSectionHeaderTitle
          >
          <ModalSectionHeaderDescription>
            Create and manage roles that define user permissions. Roles are
            assigned to users for a specific agency or agency group.
          </ModalSectionHeaderDescription>
        </ModalSectionHeader>

        <AccessRolesList ref="list" @select="openRole" />
      </ModalSection>
    </div>
  </AccessRolesModalProvider>
</template>
